import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import moment from "moment-timezone"
import { useMemo, useState } from "react"
import Card from "react-bootstrap/Card"
import Col from "react-bootstrap/Col"
import Container from "react-bootstrap/Container"
import Nav from "react-bootstrap/Nav"
import Row from "react-bootstrap/Row"
import Table from "react-bootstrap/Table"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { useTranslation } from "react-i18next"
import Select from "react-select"
import { useUrlFilter } from "../hooks/FilterHooks"
import { BenefitActiveStatus, BenefitKind } from "../models/Benefit"
import { IBenefitReport } from "../models/BenefitReport"
import { IVenue } from "../models/Venue"
import { colors } from "../theme/colors"
import { DateRangeDropdown, Selection } from "./DateRangeDropdown"

type SortConfig = {
  key: string
  direction: 'asc' | 'desc'
} | null

type BenefitTableProps = {
  data: IBenefitReport[]
  onDateFilterChange?: (selection: { startDate: Date, endDate: Date } | null) => void
  dateFilterFormatter?: (selection: Selection) => Selection
  timezone: string
  isOrganizationContext: boolean
}

enum BenefitStatusFilter {
  ACTIVE = 'active',
  ALL = 'all',
}

const getSortValue = (row: IBenefitReport, key: string): string | number => {
  switch (key) {
    case 'benefit':
      return row.benefitId.localeTitle().toLowerCase()
    case 'type':
      return row.benefitId.kind === BenefitKind.RECURRING ? 'recurring' : (row.benefitId.rules.interval || '')
    case 'venue':
      return row.restaurantId?.name?.toLowerCase() || ''
    case 'usedTotal':
      return row.usedTotal || 0
    case 'uniqueUsers':
      return row.uniqueUsers || 0
    case 'lastUsed':
      return row.lastUsed ? new Date(row.lastUsed).getTime() : 0
    case 'active':
      return row.benefitId.getActiveStatus()
    default:
      return 0
  }
}

export function BenefitTable(props: BenefitTableProps) {
  const { t } = useTranslation()
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'usedTotal', direction: 'desc' })
  const [venueFilter, setVenueFilter] = useUrlFilter<string[]>("venues")
  const [statusFilter, setStatusFilter] = useUrlFilter<string>("status", BenefitStatusFilter.ACTIVE)

  // Get unique venues for filter dropdown
  const venueOptions = useMemo(() => {
    const options: Array<{ value: string, label: string }> = []
    props.data.forEach(row => {
      const venue: IVenue = row.restaurantId
      if (venue && venue.id && venue.name) {
        const venueOption = { value: venue.id, label: venue.name }
        if (!options.some(item => item.value === venueOption.value)) {
          options.push(venueOption)
        }
      }
    })
    return options
  }, [props.data])

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = [...props.data]

    // Apply status filter
    if (statusFilter === BenefitStatusFilter.ACTIVE) {
      filtered = filtered.filter(row =>
        row.benefitId.getActiveStatus() === BenefitActiveStatus.ACTIVE
      )
    }

    // Apply venue filter
    if (venueFilter && Array.isArray(venueFilter) && venueFilter.length > 0) {
      filtered = filtered.filter(row =>
        venueFilter.includes(row.restaurantId?.id)
      )
    }

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = getSortValue(a, sortConfig.key)
        const bValue = getSortValue(b, sortConfig.key)

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }, [props.data, venueFilter, statusFilter, sortConfig])

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      }
      // Default to desc for numeric columns, asc for text
      const defaultDirection = ['usedTotal', 'uniqueUsers', 'lastUsed'].includes(key) ? 'desc' : 'asc'
      return { key, direction: defaultDirection }
    })
  }

  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) {
      return <FontAwesomeIcon className="ml-2" icon={["fal", "caret-down"]} />
    }
    return sortConfig.direction === 'desc'
      ? <FontAwesomeIcon className="ml-2" icon={["fas", "caret-down"]} />
      : <FontAwesomeIcon className="ml-2" icon={["fas", "caret-up"]} />
  }


  const activeCount = processedData.filter(row =>
    row.benefitId.getActiveStatus() === BenefitActiveStatus.ACTIVE
  ).length
  const inactiveCount = processedData.length - activeCount

  const selectedVenues = venueOptions.filter(option =>
    venueFilter && Array.isArray(venueFilter) && venueFilter.includes(option.value)
  )

  return (
    <>
      {/* Filters Card */}
      <Card className="mb-4 p-0">
        <Card.Body className="px-0 py-3">
          <Container>
            <Row>
              <Col className="text-left" style={{ width: "33%" }}>
                <DateRangeDropdown
                  timezone={props.timezone}
                  formatter={props.dateFilterFormatter}
                  onSelect={props.onDateFilterChange}
                />
              </Col>

              {/* Venue Filter */}
              {props.isOrganizationContext && (
                <Col style={{ width: "33%" }}>
                  <Select
                    placeholder={t("benefitScreen.filters.selectVenues")}
                    onChange={(entries: Array<{ value: string, label: string }> | null) => {
                      const values = entries && entries.length > 0
                        ? entries.map((o: any) => o.value)
                        : undefined
                      setVenueFilter(values)
                    }}
                    isMulti={true}
                    options={venueOptions}
                    value={selectedVenues}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Col>
              )}

              {/* Status Filter */}
              <Col style={{ width: "33%" }}>
                <Nav
                  className="justify-content-end mr-0 ml-0"
                  variant="pills"
                  activeKey={statusFilter || BenefitStatusFilter.ACTIVE}
                  onSelect={(item: string | null) => item && setStatusFilter(item)}
                >
                  <div className="d-flex flex-row border rounded border-primary overflow-hidden">
                    <Nav.Item>
                      <Nav.Link
                        style={{ borderRadius: 0, padding: "0.375rem 0.75rem" }}
                        eventKey={BenefitStatusFilter.ACTIVE}
                      >
                        {t("benefitScreen.active")}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        style={{ borderRadius: 0, padding: "0.375rem 0.75rem" }}
                        eventKey={BenefitStatusFilter.ALL}
                      >
                        {t("benefitScreen.all")}
                      </Nav.Link>
                    </Nav.Item>
                  </div>
                </Nav>
              </Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>

      {/* Summary */}
      <div className="mb-2 text-center font-size-medium font-heading font-weight-light">
        {t("benefitScreen.tableRowSummary", {
          totalCount: processedData.length,
          activeCount: activeCount,
          inactiveCount: inactiveCount,
        })}
      </div>

      {/* Table */}
      <Card className="m-0" style={{ borderWidth: 0, borderColor: colors.boxFill }}>
        <div className="rounded-sm">
          <Table className="benefitTable p-0 m-0" striped hover>
            <thead className="text-primary">
              <tr style={{ MozUserSelect: "none", WebkitUserSelect: "none" }}>
                <th
                  style={{
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    whiteSpace: "nowrap",
                    width: 100,
                    borderWidth: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('type')}
                >
                  {t("benefitScreen.tableHeaders.type")}
                  {getSortIcon('type')}
                </th>

                <th
                  style={{
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    whiteSpace: "nowrap",
                    width: 350,
                    borderWidth: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('benefit')}
                >
                  <span className="text-left">{t("benefitScreen.tableHeaders.benefit")}</span>
                  {getSortIcon('benefit')}
                </th>

                <th
                  style={{
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    whiteSpace: "nowrap",
                    width: 350,
                    borderWidth: 0
                  }}
                >
                  <span className="text-left"></span>
                </th>

                {props.isOrganizationContext && (
                  <th
                    style={{
                      MozUserSelect: "none",
                      WebkitUserSelect: "none",
                      whiteSpace: "nowrap",
                      borderWidth: 0,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort('venue')}
                  >
                    {t("benefitScreen.tableHeaders.venue")}
                    {getSortIcon('venue')}
                  </th>
                )}

                <th
                  style={{
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    whiteSpace: "nowrap",
                    borderWidth: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('usedTotal')}
                >
                  {t("benefitScreen.tableHeaders.activations")}
                  {getSortIcon('usedTotal')}
                </th>

                <th
                  style={{
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    whiteSpace: "nowrap",
                    borderWidth: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('lastUsed')}
                >
                  {t("benefitScreen.tableHeaders.lastActivation")}
                  {getSortIcon('lastUsed')}
                </th>

                <th
                  style={{
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    whiteSpace: "nowrap",
                    width: 40,
                    borderWidth: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('active')}
                >
                  {t("benefitScreen.tableHeaders.status")}
                  {getSortIcon('active')}
                </th>
              </tr>
            </thead>

            <tbody>
              {processedData.map((row: IBenefitReport, index: number) => (
                <tr key={index}>
                  {/* Type Cell */}
                  <td>
                    <div className="d-flex flex-column text-primary p-0 pl-1">
                      <div className="pt-1 text-center small">
                        {row.benefitId.kind === BenefitKind.RECURRING
                          ? t("benefit.intervals.recurring")
                          : t("benefit.intervals." + row.benefitId.rules.interval)}
                      </div>
                    </div>
                  </td>

                  {/* Benefit Cell */}
                  <td>
                    <div className="font-heading text-left" style={{
                      fontWeight: row.benefitId.getActiveStatus() === BenefitActiveStatus.ACTIVE ? 'bold' : 'normal'
                    }}>
                      {row.benefitId.localeTitle()}
                    </div>
                  </td>

                  {/* Image Cell */}
                  <td>
                    {row.benefitId.images?.mainImage ? (
                      <img
                        style={{ width: 90, height: 54, objectFit: 'cover', borderRadius: '5px' }}
                        src={row.benefitId.images.mainImage}
                        alt=""
                      />
                    ) : (
                      <div />
                    )}
                  </td>

                  {/* Venue Cell */}
                  {props.isOrganizationContext && (
                    <td>
                      <div className="text-menugray">{row.restaurantId?.name || ''}</div>
                    </td>
                  )}

                  {/* Activations Cell */}
                  <td>
                    <div className="d-flex text-center flex-column align-items-center justify-content-center p-0 m-0">
                      <div className="p-0 m-0 text-primary font-heading font-size-large">{row.usedTotal || 0}</div>
                    </div>
                  </td>

                  {/* Date Cell */}
                  <td>
                    <div className="text-left">
                      {row.lastUsed ? moment.utc(row.lastUsed).tz("Europe/Helsinki").format("DD.MM.YYYY") : "-"}
                    </div>
                  </td>

                  {/* Status Cell */}
                  <td>
                    <div className="text-center d-flex flex-column align-items-center justify-content-center">
                      {row.benefitId.getActiveStatus() === BenefitActiveStatus.ACTIVE ? (
                        <div style={{
                          display: 'inline-block',
                          height: 23,
                          width: 23,
                          background: colors.green,
                          borderRadius: '50%',
                        }}>
                          <FontAwesomeIcon
                            style={{ color: colors.white, fontSize: 18 }}
                            icon={["fal", "check"]}
                          />
                        </div>
                      ) : (
                        <FontAwesomeIcon
                          className="text-hentopinkki icon-medium"
                          icon={["fal", "minus-circle"]}
                        />
                      )}

                      {/* Show card icon if venue_status exists */}
                      {row.benefitId.linkedVenueStatuses.length > 0 && (
                        <FontAwesomeIcon
                          className="text-primary"
                          style={{ fontSize: 16, marginTop: '8px' }}
                          icon={["fal", "credit-card"]}
                          title={t("benefitScreen.aiInsightsItemTitle", { count: row.benefitId.linkedVenueStatuses.length })}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>
    </>
  )
}
